// WbsTree.js
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  useGetWbsLevelsQuery,
  useLazyGetWbsLevelsQuery
} from "store/slices/apis/scheduleSlice";

// Map API response -> [{ id, name, raw, isLeaf }]
function mapResponseToItems(res, parentId = null) {
  if (!res || !Array.isArray(res.data)) return [];

  return res.data.map(function (item) {
    return {
      id: item.id,
      name: item.name,
      parentId: parentId,
      raw: item,
      isLeaf: item.isLeaf === true
    };
  });
}

function getFullWbsPath(node, nodeMap) {
  const namePath = [];
  const idPath = [];
  let current = node;

  while (current) {
    namePath.unshift(current.name);
    idPath.unshift(current.id);
    current = current.parentId ? nodeMap[current.parentId] : null;
  }

  return {
    namePath: namePath.join(" > "),
    idPath: idPath.join(","),
  }
}

const WbsTreeView = ({ projectId, parentId, onSelect, containerStyle = {} }) => {
  // ROOT LEVEL
  const {
    data: rootData,
    isLoading: isRootLoading,
    isError: isRootError
  } = useGetWbsLevelsQuery({ projectId: projectId, parentId: parentId });

  const rootItems = mapResponseToItems(rootData, parentId);

  useEffect(() => {
    if (!rootData || !Array.isArray(rootData.data)) return;

    setNodeMap(prev => {
      const map = { ...prev };
      rootData.data.forEach(item => {
        map[item.id] = {
          id: item.id,
          name: item.name,
          parentId: null,
          raw: item,
          isLeaf: item.isLeaf === true
        };
      });
      return map;
    });
  }, [rootData]);

  // CHILDREN (lazy)
  const [trigger] = useLazyGetWbsLevelsQuery();
  const [childrenMap, setChildrenMap] = useState({});   // parentId -> [nodes]
  const [loadingMap, setLoadingMap] = useState({});     // parentId -> bool
  const [expandedMap, setExpandedMap] = useState({});   // nodeId -> bool
  const [nodeMap, setNodeMap] = useState({});

  const loadChildren = async function (parentId) {
    setLoadingMap(function (prev) {
      return { ...prev, [parentId]: true };
    });

    try {
      const res = await trigger({
        projectId: projectId,
        parentId: parentId
      }).unwrap();

      const items = mapResponseToItems(res, parentId);

      setChildrenMap(function (prev) {
        return { ...prev, [parentId]: items };
      });

      setNodeMap(prev => {
        const map = { ...prev };
        items.forEach(n => (map[n.id] = n));
        return map;
      });

    } catch (err) {
      console.error("Error loading children for parentId", parentId, err);
      setChildrenMap(function (prev) {
        return { ...prev, [parentId]: [] };
      });
    }

    setLoadingMap(function (prev) {
      return { ...prev, [parentId]: false };
    });
  };

  const toggleNode = function (node) {
    const nodeId = node.id;
    const isExpanded = !!expandedMap[nodeId];

    // Collapse
    if (isExpanded) {
      setExpandedMap(function (prev) {
        return { ...prev, [nodeId]: false };
      });
      return;
    }

    // Expand
    setExpandedMap(function (prev) {
      return { ...prev, [nodeId]: true };
    });

    // If children not loaded yet, fetch
    if (childrenMap[nodeId] === undefined && !node.isLeaf) {
      loadChildren(nodeId);
    }
  };

  const renderNodes = function (items, level) {
    if (!items || items.length === 0) return null;

    return (
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          paddingLeft: level === 0 ? 0 : 16
        }}
      >
        {items.map(function (node) {
          const nodeId = node.id;
          const isExpanded = !!expandedMap[nodeId];
          const isLoading = !!loadingMap[nodeId];
          const children = childrenMap[nodeId] || [];

          return (
            <li key={nodeId}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 0.3,
                  ml: level * 0.5
                }}
              >
                {/* expand/collapse button (only if not leaf) */}
                {!node.isLeaf && (
                  <IconButton
                    size="small"
                    onClick={function () {
                      toggleNode(node);
                    }}
                  >
                    {isExpanded ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </IconButton>
                )}

                {/* indent for leaf nodes to align with icon */}
                {node.isLeaf && (
                  <Box sx={{ width: 24 /* icon space */ }} />
                )}

                {/* node label */}
                <Typography
                  sx={{
                    fontSize: 14,
                    cursor: "pointer",
                    color: "#111"
                  }}
                  onClick={function () {
                    if (onSelect) {
                      const { namePath, idPath } = getFullWbsPath(node, nodeMap);
                      onSelect({
                        ...node,
                        fullPath: namePath,
                        idPath: idPath,
                      }); // { id, name, raw, isLeaf }
                    }
                  }}
                >
                  {node.name}
                </Typography>

                {/* loading indicator */}
                {isLoading && (
                  <CircularProgress size={14} sx={{ ml: 1 }} />
                )}
              </Box>

              {/* children list */}
              {isExpanded && children && children.length > 0 && (
                renderNodes(children, level + 1)
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  if (isRootLoading) {
    return <CircularProgress />;
  }

  if (isRootError) {
    return <div>Failed to load WBS tree.</div>;
  }

  return (
    <Box
      sx={{
        // border: "1px solid #ddd",
        borderRadius: 1,
        p: 1,
        maxHeight: 400,
        overflowY: "auto",
        backgroundColor: "#fafafa",
        ...containerStyle
      }}
    >

      {rootItems.length === 0 ? (
        <div>No WBS data found.</div>
      ) : (
        renderNodes(rootItems, 0)
      )}
    </Box>
  );
};

export default WbsTreeView;
